import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import get from 'lodash/get';
import { handleSubmissionErrors } from '../../../../utils/errors'
import Layout from '../../../../layout';
import modalTitles from '../../../../utils/modal-title-options';
import ctaTitleOptions from '../../../../utils/cta-title-options-tiled-promos';
import * as propertyActions from '../../../../actions/properties';
import * as tiledPromoActions from '../../../../actions/tiledPromos';
import * as sidebarActions from '../../../../actions/sidebar';
import * as imageActions from '../../../../actions/images';
import HotelMeetingForm from '../../../../components/HotelMeetingForm';
import FormNavigator from '../../../../components/FormNavigator';

const propTypes = {
  hotel: PropTypes.object.isRequired,
  dining: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  images: PropTypes.array.isRequired,
  updateTiledPromo: PropTypes.func.isRequired,
  fetchProperty: PropTypes.func.isRequired,
  fetchTiledPromo: PropTypes.func.isRequired,
  updateSidebar: PropTypes.func.isRequired,
  setCurrentImage: PropTypes.func.isRequired
};

const sections = [
  { id: 'title', title: 'Title & Content' },
  { id: 'date', title: 'Date & Time' },
  { id: 'image', title: 'Set an Image' },
  { id: 'upload', title: 'Upload a PDF' },
  { id: 'cta', title: 'CTA (Call to Action)' },
  { id: 'preview', title: 'Preview + Publish' },
];

const PrivateDiningEdit = ({
  hotel,
  dining,
  images,
  isLoading,
  match: {
    params: { hotelid, id }
  },
  history: { push },
  updateTiledPromo,
  fetchProperty,
  fetchTiledPromo,
  updateSidebar,
  setCurrentImage
}) => {
  useEffect(() => {
    fetchProperty(hotelid).then(property => {
      updateSidebar({ titles: { Hotel: property.name } });
    });
    fetchTiledPromo(hotelid, id);
    return () => {};
  }, []);

  useEffect(() => {
    get(dining, 'images', []).forEach(image => {
      setCurrentImage('hotel-promos-image', image);
    });
    return () => {};
  }, [dining]);

  const handleSubmit = data => {
    /* eslint-disable no-param-reassign */
    let { pdf } = data;
    pdf = pdf && pdf.id ? { assetId: pdf.id } : null;

    const update = {
      ...data,
      pdf,
      startsOn: data.startsOn?.toJSON(),
      endsOn: data.endsOn ? data.endsOn.toJSON() : data.endsOn,
      images,
      modal1Title:
        // eslint-disable-next-line no-nested-ternary
        data.modal1Title === 'None'
          ? null
          : data.modal1Title === 'Other'
          ? data.modal1TitleOther
          : data.modal1Title,
      modal2Title:
        // eslint-disable-next-line no-nested-ternary
        data.modal2Title === 'None'
          ? null
          : data.modal2Title === 'Other'
          ? data.modal2TitleOther
          : data.modal2Title,
      cta: Object.assign(
        data.cta,
        data.cta.type === 'Other' && { type: data.cta.typeInput }
      )
    };
    return updateTiledPromo(hotelid, id, update)
      .then(() => push(`/hotels/${hotelid}/tiled/dining`))
      .catch(e => {
        handleSubmissionErrors(e)
      });
  };
  return (
    <Layout metaTitle="Edit Private Dining Promo">
      {isLoading ? (
        <div>Loading ...</div>
      ) : (
        <div className="w-full max-w-3xl">
          <Link
            to={`/hotels/${hotelid}/tiled/dining`}
            className="mb-8 inline-block text-sm text-grey-dark hover:text-grey-darkest no-underline"
          >
            &lt; Back to Private Dining Promos
          </Link>
          <h1 className="mb-6">Edit Private Dining Promo</h1>
          <div className="mb-16">
            Use this page to edit {dining.title} Private Dining Promo on{' '}
            {hotel.name}.
          </div>
          <HotelMeetingForm
            id="edit-dinings-form"
            category="Private Dining"
            hotel={hotel}
            initialValues={{
              ...dining,
              // eslint-disable-next-line no-nested-ternary
              modal1Title: !dining.modal1Title
                ? 'None'
                : modalTitles.findIndex(
                    title => title.id === dining.modal1Title
                  ) === -1
                ? 'Other'
                : dining.modal1Title,
              modal1TitleOther:
                modalTitles.findIndex(
                  title => title.id === dining.modal1Title
                ) === -1
                  ? dining.modal1Title
                  : undefined,
              // eslint-disable-next-line no-nested-ternary
              modal2Title: !dining.modal2Title
                ? 'None'
                : modalTitles.findIndex(
                    title => title.id === dining.modal2Title
                  ) === -1
                ? 'Other'
                : dining.modal2Title,
              modal2TitleOther:
                modalTitles.findIndex(
                  title => title.id === dining.modal2Title
                ) === -1
                  ? dining.modal2Title
                  : undefined,
              showmodal2: !!dining.modal2Title,
              cta: dining.cta && {
                ...dining.cta,
                type:
                  ctaTitleOptions.findIndex(
                    title => title.id === get(dining, 'cta.type')
                  ) === -1
                    ? 'Other'
                    : get(dining, 'cta.type'),
                typeInput:
                  ctaTitleOptions.findIndex(
                    title => title.id === get(dining, 'cta.type')
                  ) === -1
                    ? get(dining, 'cta.type')
                    : undefined
              },
              startsOn: dining.startsOn
                ? new Date(dining.startsOn)
                : dining.startsOn,
              endsOn: dining.endsOn ? new Date(dining.endsOn) : dining.endsOn
            }}
            onSubmit={handleSubmit}
            enableReinitialize
            keepDirtyOnReinitialize
          />
        </div>
      )}
      <FormNavigator
        id="edit-dinings-navigator"
        sections={sections}
        className="hidden xl:block mt-32 pt-16"
      />
    </Layout>
  );
};

PrivateDiningEdit.propTypes = propTypes;

export default connect(
  (state, props) => ({
    hotel: state.property.data,
    dining: state.tiledPromo.data,
    images: get(state, 'images.hotel-promos-image.items', [])
      .filter(item => item.file)
      .map(item => ({ assetId: item.file.id, alt: item.alt })),
    isLoading: state.property.isLoading || state.tiledPromo.isLoading
  }),
  {
    ...propertyActions,
    ...sidebarActions,
    ...tiledPromoActions,
    ...imageActions
  }
)(PrivateDiningEdit);
